import { Box, Typography, Container } from "@mui/material";
import React, { useState, useEffect, useCallback } from "react";
import TreffasContainer from "src/components/TreffasContainer";
import IsMounted from "src/components/IsMounted";
import Loading from "src/components/Loading";
import { useParams } from "react-router-dom";
import http from "src/utils/http";
import PageNotFound from "src/components/PageNotFound";
import SL from "src/assets/images/survey-thank.png";
import PowerdBy from "../PowerdBy";
import Constant from "src/utils/Constant";
import { useTranslation } from "react-i18next";

const Thankyou = () => {
  const { surveyId, surveySlug } = useParams();
  const [details, setDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const mounted = IsMounted();
  // const navigate = useNavigate();
  const { t } = useTranslation();

  const getSurveyDetails = useCallback(() => {
    http
      .get("/api/general/survey/details", {
        params: {
          survey_id: surveyId,
          slug: surveySlug,
        },
      })
      .then((response) => {
        if (mounted.current) {
          setDetails(response.data.data);
        }
      })
      .catch((err) => console.log("error:", err.message))
      .finally(() => setIsLoading(false));
  }, [mounted, surveyId, surveySlug]);

  useEffect(() => {
    getSurveyDetails();
  }, [getSurveyDetails]);

  if (isLoading) {
    return <Loading />;
  }

  if (!details) {
    return <PageNotFound />;
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#F8F9FA",
      }}
    >
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          py: { xs: 4, sm: 6, md: 8 },
          px: { xs: 2, sm: 3 },
        }}
      >
        <TreffasContainer layout="tp"
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Logo Section */}
          {details?.logo && (
            <Box
              sx={{
                mb: { xs: 3, sm: 4, md: 5 },
                display: "flex",
                justifyContent: "center",
                animation: "fadeInDown 0.6s ease-out",
                "@keyframes fadeInDown": {
                  from: {
                    opacity: 0,
                    transform: "translateY(-20px)",
                  },
                  to: {
                    opacity: 1,
                    transform: "translateY(0)",
                  },
                },
              }}
            >
              <Box
                sx={{
                  p: 2,
                  borderRadius: "12px",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                }}
              >
                <img
                  alt="Survey logo"
                  src={Constant.getFileInStorage("coach/survey", details?.logo)}
                  style={{
                    height: "auto",
                    maxHeight: "80px",
                    maxWidth: "200px",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>
          )}

          {/* Thank You Card */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "640px",
              backgroundColor: "#FFFFFF",
              borderRadius: "16px",
              boxShadow: "0 4px 24px rgba(0, 0, 0, 0.08)",
              border: "1px solid #E8E9ED",
              p: { xs: 4, sm: 5, md: 6 },
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              overflow: "hidden",
              animation: "fadeInUp 0.8s ease-out",
              "@keyframes fadeInUp": {
                from: {
                  opacity: 0,
                  transform: "translateY(30px)",
                },
                to: {
                  opacity: 1,
                  transform: "translateY(0)",
                },
              },
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #667EEA 0%, #764BA2 100%)",
              },
            }}
          >
            {/* Success Icon/Image */}
            <Box
              sx={{
                mb: 3,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                animation: "scaleIn 0.6s ease-out 0.2s both",
                "@keyframes scaleIn": {
                  from: {
                    opacity: 0,
                    transform: "scale(0.8)",
                  },
                  to: {
                    opacity: 1,
                    transform: "scale(1)",
                  },
                },
              }}
            >
              <Box
                sx={{
                  width: { xs: "100px", sm: "120px" },
                  height: { xs: "100px", sm: "120px" },
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "50%",
                  backgroundColor: "#F0F4FF",
                  p: 2,
                }}
              >
                <img
                  src={SL}
                  alt={"Thank you"}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              </Box>
            </Box>

            {/* Thank You Title */}
            <Typography
              sx={{
                fontFamily: "var(--font-grenettepromedium)",
                fontSize: { xs: "24px", sm: "28px", md: "32px" },
                fontWeight: 600,
                lineHeight: 1.2,
                color: "#060A2D",
                textAlign: "center",
                mb: 2,
                textTransform: "capitalize",
              }}
            >
              {t("survey.take.form.alert.thank_you")}
            </Typography>

            {/* Thank You Message */}
            {details?.thankyou_message && (
              <Typography
                sx={{
                  fontSize: { xs: "15px", sm: "16px" },
                  fontWeight: 400,
                  lineHeight: 1.6,
                  color: "#6A6E91",
                  textAlign: "center",
                  maxWidth: "520px",
                  mt: 1,
                }}
              >
                {details?.thankyou_message}
              </Typography>
            )}

            {/* Decorative Elements */}
            <Box
              sx={{
                position: "absolute",
                top: -50,
                right: -50,
                width: "200px",
                height: "200px",
                borderRadius: "50%",
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)",
                zIndex: 0,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                bottom: -30,
                left: -30,
                width: "150px",
                height: "150px",
                borderRadius: "50%",
                background:
                  "linear-gradient(135deg, rgba(102, 126, 234, 0.08) 0%, rgba(118, 75, 162, 0.08) 100%)",
                zIndex: 0,
              }}
            />
          </Box>
        </TreffasContainer>
      </Box>

      {/* Powered by */}
      <Box
        sx={{
          mt: "auto",
        }}
      >
        <PowerdBy />
      </Box>
    </Box>
  );
};

export default Thankyou;
